import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "../hooks/useToast";
import { AnimatePresence } from 'motion/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/common/Card';
import { Button } from '../components/common/Button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../components/common/Table';
import {
  PlusIcon,
  TimerIcon,
  PlayIcon,
  StopIcon,
  Pencil1Icon,
} from '@radix-ui/react-icons';
import { getActivitySummary, updateActivity } from '../api/activity';
import { startTimer, stopTimer } from '../api/activity-log';
import { formatDuration } from '../utils/time';
import Layout from '../components/common/Layout';
import QuickAddActivity from '../components/activity/QuickAddActivity';
import { Modal } from '../components/common/Modal';
import { Input } from '../components/common/Input';

export default function ActivitiesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [editForm, setEditForm] = useState({
    name: ''
  });

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const data = await getActivitySummary();
      setActivities(data);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to fetch activities',
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTimerToggle = async (activityId, inProgress) => {
    try {
      if (inProgress) {
        await stopTimer(activityId);
      } else {
        await startTimer(activityId);
      }
      toast({
        title: "Success",
        description: inProgress ? 'Timer stopped' : 'Timer started',
        type: "success"
      });
      await fetchActivities();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to toggle timer',
        type: "error"
      });
    }
  };

  const handleViewSingleActivity = (activityId) => {
    navigate(`/activity/${activityId}/view`);
  };

  const handleActivityAdded = (newActivityId, name) => {
    const newActivity = {
      id: newActivityId,
      name: name,
      in_progress: true,
      total_duration_in_minutes: 0,
    };
    setActivities([newActivity, ...activities]);
  };

  const handleEditClick = (activity) => {
    setSelectedActivity(activity);
    setEditForm({ name: activity.name });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateActivity(selectedActivity.id, editForm.name);
      
      // Update local state
      const updatedActivities = activities.map(activity =>
        activity.id === selectedActivity.id
          ? { ...activity, name: editForm.name }
          : activity
      );
      setActivities(updatedActivities);

      toast({
        title: "Success",
        description: "Activity name updated successfully",
        type: "success"
      });
      setShowEditModal(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update activity name",
        type: "error"
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Activities</h1>
            <p className="text-muted-foreground">
              Manage and track your activities
            </p>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className={showAddForm ? "button-glow-active" : "button-glow"}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            New Activity
          </Button>
        </div>

        {/* Quick Add Form */}
        <AnimatePresence>
          {showAddForm && (
            <QuickAddActivity
              onClose={() => setShowAddForm(false)}
              onActivityAdded={handleActivityAdded}
            />
          )}
        </AnimatePresence>

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Total Activities
              </CardTitle>
              <TimerIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activities.length}</div>
              <p className="text-xs text-muted-foreground">
                Across all categories
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Active Timers
              </CardTitle>
              <PlayIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {activities.filter(a => a.in_progress).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Currently running
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Recent Activity
              </CardTitle>
              <TimerIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold line-clamp-1">
                {activities.length > 0 ? activities[0].name : 'No activities'}
              </div>
              {/* <p className="text-xs text-muted-foreground">
                Most recent activity
              </p> */}
            </CardContent>
          </Card>
        </div>

        {/* Activities Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Activities</CardTitle>
            <CardDescription>
              Click on an activity to view more details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Total Time</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activities.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="h-24 text-center text-muted-foreground"
                        >
                          No activities found. Create your first activity to get started!
                        </TableCell>
                      </TableRow>
                    ) : (
                      activities.map((activity) => (
                        <TableRow 
                          key={activity.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleViewSingleActivity(activity.id)}
                        >
                          <TableCell className="font-medium">
                            {activity.name}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  activity.in_progress
                                    ? 'bg-green-500'
                                    : 'bg-muted'
                                }`}
                              />
                              {activity.in_progress ? 'Active' : 'Inactive'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {formatDuration(activity.total_duration_in_minutes || 0)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTimerToggle(activity.id, activity.in_progress);
                                }}
                              >
                                {activity.in_progress ? (
                                  <StopIcon className="h-4 w-4 text-destructive" />
                                ) : (
                                  <PlayIcon className="h-4 w-4 text-primary" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(activity);
                                }}
                              >
                                <Pencil1Icon className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Activity Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
        >
          <form onSubmit={handleEditSubmit}>
            <Modal.Header>
              <Modal.Title>Edit Activity</Modal.Title>
            </Modal.Header>
            <div className="pt-6 pb-6">
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ name: e.target.value })}
                placeholder="Activity name"
              />
            </div>
            <Modal.Footer>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save Changes
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </Layout>
  );
} 